// #686 — runtime probe for the demo-mode sign-in gate.
//
// The gate that decides whether App.tsx shows CD's demo persona chooser
// instead of redirecting to the tenant's real SSO login used to be baked at
// COMPILE time (`VITE_DEMO_MODE`, set from tenant-manifest `tenant.demoMode`
// at compose). The fleet admin toggle writes live D1 `tenants.demo_mode` —
// which ALREADY gates the `/demo/personas` / `/demo/sign-in` backend routes
// per request (`workers/identity/src/routes/demo.ts`,
// `isTenantDemoModeEnabled`) — but that compile-time flag never re-read it,
// so an operator flip only took effect after the next recompose/redeploy.
//
// This hook closes that gap by reusing the SAME already-wired, unauthenticated
// op CD's own Signin persona chooser calls: `getDemoPersonas()`
// (`@v0y4ge/api-client`, GET `/t/:tenantSlug/demo/personas`). It resolves
// (200, even with an empty catalog) when `demo_mode` is on and throws
// `ApiError` with `status === 404` when it's off (see demo.ts's `notFound()` — the SAME 404
// shape as "tenant unknown", by design, so an off-tenant can't be
// distinguished from a nonexistent one). Only that explicit 404, or a 200
// with an empty catalog, may send a visitor to the real login.
//
// Any OTHER failure -- a timeout, a network or CORS error, a 5xx, a response
// that does not parse -- says nothing about demo_mode; it says the identity
// worker could not answer. Treating it as OFF redirected every fresh visitor
// to a demo tenant to the real login whenever identity was slow. So such a
// failure is retried a bounded number of times (`DEMO_MODE_PROBE_RETRY_DELAYS_MS`),
// and if every attempt fails the hook reports `unreachable: true`. The host
// then does not redirect; it passes the state to CD's app, whose sign-in
// screen shows its "couldn't reach sign-in" state, with a Try again action
// that calls `retry()`.
// `retry()` returns a promise that settles when that re-check has an answer,
// and `unreachable` stays true while it runs, so the screen can keep its button
// busy instead of being swapped for the loading placeholder. The chooser is
// still never exposed on an inconclusive probe: `enabled` stays false.
//
// Bounded hang guard: this probe gates the ENTIRE sign-in render for every
// tenant, so a connection that hangs open (cold worker, stalled D1) must never
// leave `loading` stuck at true. `getDemoPersonas()` takes no cancellation
// signal, so each attempt is bounded by `DEMO_MODE_PROBE_TIMEOUT_MS`; a slow
// response that arrives after its attempt timed out is ignored, so it can never
// flip the gate once the render has moved on.
//
// No `configureApiClient()` call is needed here: `getDemoPersonas()` resolves
// the identity base + tenant from the SAME build-time env vars
// (`VITE_IDENTITY_BASE_URL` / `VITE_IDENTITY_TENANT_SLUG`) App.tsx already
// reads directly, via api-client's own env fallback
// (`ENV_IDENTITY_BASE`/`ENV_TENANT` in `client.ts`) — nothing in this app
// calls `configureApiClient`, so those env fallbacks are exactly what's live.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, getDemoPersonas } from "@v0y4ge/api-client";

// Mirrors useWhoami's WHOAMI_TIMEOUT_MS (same identity worker, same bound).
// Exported so tests advance fake timers by exactly this amount.
export const DEMO_MODE_PROBE_TIMEOUT_MS = 8000;

// Wait before each RETRY of a failed attempt (timeout, network error, 5xx).
// Its length is the number of retries: two retries after the first attempt.
export const DEMO_MODE_PROBE_RETRY_DELAYS_MS: readonly number[] = [1000, 2000];

export interface DemoModeState {
  /** True while the probe (including its retries) is in flight. */
  loading: boolean;
  /**
   * Authoritative, live `tenants.demo_mode` value for this tenant, resolved by
   * calling the same `/demo/personas` op the persona chooser uses. False while
   * the probe is unreachable: the chooser is never exposed on an inconclusive
   * answer.
   */
  enabled: boolean;
  /** True when a successful probe returns no personas; false before a result or on failure. */
  empty: boolean;
  /**
   * True when every attempt failed with something other than a 404 (timeout,
   * network error, 5xx). The demo_mode answer is UNKNOWN: for a signed-out
   * visitor, the host must not redirect to the real login; it hands the state
   * to CD's app, which shows its sign-in screen in the unreachable state. It
   * stays true while a `retry()` re-check is running, and changes only once
   * that re-check answers.
   */
  unreachable: boolean;
  /**
   * Re-run the probe from the first attempt (CD's Try again action). The
   * promise settles when that run has an answer (enabled, off, or unreachable
   * again); it never rejects.
   */
  retry: () => Promise<void>;
}

/**
 * Runtime probe for the demo-mode gate.
 *
 * `identityBaseUrl` absent (e.g. local dev without an identity worker
 * configured) short-circuits to the hint with loading:false and empty:false.
 * Otherwise fires `getDemoPersonas()` on mount (and again when
 * `identityBaseUrl`/`tenantSlug` change, or `retry()` is called) and resolves
 * to the live boolean, retrying failures that are not an explicit 404.
 */
export function useDemoMode(identityBaseUrl: string, tenantSlug: string, hint = false): DemoModeState {
  const [runKey, setRunKey] = useState(0);
  // Callers of retry() waiting for the run it started to answer.
  const waiters = useRef<Array<() => void>>([]);
  // Set by retry(): the next run keeps an unreachable state on screen until it answers.
  const runIsRetry = useRef(false);
  const settleWaiters = useCallback(() => {
    const pending = waiters.current;
    waiters.current = [];
    for (const resolve of pending) resolve();
  }, []);
  const retry = useCallback(
    () =>
      new Promise<void>((resolve) => {
        waiters.current.push(resolve);
        runIsRetry.current = true;
        setRunKey((key) => key + 1);
      }),
    [],
  );
  const [state, setState] = useState<Omit<DemoModeState, "retry">>({
    loading: Boolean(identityBaseUrl),
    enabled: hint,
    empty: false,
    unreachable: false,
  });

  // A pending retry() must not hang if the host unmounts mid-run.
  useEffect(() => settleWaiters, [settleWaiters]);

  useEffect(() => {
    // Cleared when the run answers, not here, so a run that is restarted before
    // it answers (React re-running the effect) is still treated as the retry.
    const isRetry = runIsRetry.current;
    const answer = (next: Omit<DemoModeState, "retry">) => {
      runIsRetry.current = false;
      setState(next);
      settleWaiters();
    };
    if (!identityBaseUrl) {
      answer({ loading: false, enabled: hint, empty: false, unreachable: false });
      return;
    }

    let cancelled = false;
    const timers = new Set<number>();
    const starting = { loading: true, enabled: hint, empty: false, unreachable: false };
    // A retry from the unreachable state keeps that state until it has an
    // answer; any other run starts from loading.
    setState((previous) => (isRetry && previous.unreachable ? previous : starting));

    const attempt = (index: number) => {
      let settled = false;

      // A failure that is not an explicit 404: retry while retries remain,
      // then report the probe as unreachable (never as demo OFF).
      const failed = () => {
        const delay = DEMO_MODE_PROBE_RETRY_DELAYS_MS[index];
        if (delay !== undefined) {
          const retryId = window.setTimeout(() => {
            timers.delete(retryId);
            if (!cancelled) attempt(index + 1);
          }, delay);
          timers.add(retryId);
          return;
        }
        answer({ loading: false, enabled: false, empty: false, unreachable: true });
      };

      const timeoutId = window.setTimeout(() => {
        timers.delete(timeoutId);
        if (cancelled || settled) return;
        settled = true;
        failed();
      }, DEMO_MODE_PROBE_TIMEOUT_MS);
      timers.add(timeoutId);

      const settle = (): boolean => {
        if (cancelled || settled) return false;
        settled = true;
        window.clearTimeout(timeoutId);
        timers.delete(timeoutId);
        return true;
      };

      void getDemoPersonas()
        .then((personas) => {
          if (!settle()) return;
          // A 200 response IS "demo_mode is on": the route's own gate decided
          // that. An empty catalog separately tells the shell to use the real
          // sign-in.
          answer({ loading: false, enabled: true, empty: personas.length === 0, unreachable: false });
        })
        .catch((error) => {
          if (!settle()) return;
          if (error instanceof ApiError && error.status === 404) {
            // The explicit OFF answer: demo_mode is off (or the tenant is unknown).
            answer({ loading: false, enabled: false, empty: false, unreachable: false });
            return;
          }
          failed();
        });
    };

    attempt(0);

    return () => {
      cancelled = true;
      for (const id of timers) window.clearTimeout(id);
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `hint` is a
    // first-paint seed only; re-running the probe on every hint change would
    // defeat the point (the probe result always wins over the hint).
  }, [identityBaseUrl, tenantSlug, runKey]);

  return { ...state, retry };
}
